import classNames from 'classnames';
import map from 'lodash/map';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { Col, Container, Row } from 'reactstrap';
import { compose } from 'redux';
import InlineSVG from 'svg-inline-react';

import pillCrossed from 'public/pill-crossed.svg';
import pillTall from 'public/pill-tall2.svg';
import pillTriangle from 'public/pill-triangle.svg';
import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import { useSegmentTrack } from 'src/app/hooks/useSegmentTrack';
import { blog, getStarted } from 'src/app/router/paths';
import { segmentTrack } from 'src/app/state/actions';
import type { Dispatch } from 'src/app/state/store';

import styles from './styles.module.scss';

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
});

const connector = connect(null, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;
interface AboutProps extends PropsFromRedux {}

const About = ({ dispatchSegmentTrack }: AboutProps) => {
  useSegmentTrack(UNIFIED.viewAboutUs);

  const handleClickBlogLink = () => {
    dispatchSegmentTrack(UNIFIED.viewBlog, {});
  };

  const cells = [
    {
      title: <div className="font-label-callout font-down-3 pb-3 pt-5">OUR PHILOSOPHY</div>,
      header: (
        <h2 className="red pt-4 font-h-3 pb-3">
          More &ldquo;I love you.&rdquo;
          <br />
          Less &ldquo;next please.&rdquo;
        </h2>
      ),
      body: (
        <div className="pb-4">
          <p className="body pb-3 col-md-10 px-0">
            We believe a pharmacy can be focused on caring. Does that sound crazy? It shouldn&#8217;t. And that&#8217;s
            why we&#8217;re dismantling the old pharmacy and completely rebuilding it. We believe the pharmacy can work
            for the people. All people. That includes patients, doctors and insurers.
          </p>

          <a
            href={blog.route}
            onClick={handleClickBlogLink}
            target="_blank"
            rel="noopener noreferrer"
            className="link-btn link-btn__primary link-btn__medium btn--mobile-fw"
          >
            Visit our blog
          </a>
        </div>
      ),
      mobileImage: '/public/cell-1-generic-mobile@2x.jpg',
    },
    {
      title: <div className="font-label-callout font-down-3 pt-5">OUR PEOPLE</div>,
      header: (
        <h2 className="red font-h-3 pt-4 pb-4">
          Sonia cares.
          <br />
          Don&#8217;t be alarmed.
        </h2>
      ),
      body: (
        <Col md={10} className="pb-6 px-0">
          <p className="body">
            This is Sonia, she&#8217;s our Chief Pharmacist. She&#8217;s also a Doctor of Pharmacy, so please call her
            Dr. Sonia. Just kidding, she&#8217;s not like that. Call her Sonia. Her decades of experience working in
            traditional pharmacies have made her all too familiar with the frustrations of patients. She and her team
            are so excited to show you just how a new approach to pharmacy can make things easier.
          </p>
        </Col>
      ),
      mobileImage: '/public/cell-2-mobile@2x.jpg',
    },
    {
      title: <div className="font-label-callout font-down-3 mt-4 pt-3">OUR PHARMACY</div>,
      header: (
        <h2 className="red mt-4 pb-5 font-h-3">
          Pharmacy of
          <br />
          the future
        </h2>
      ),
      body: (
        <div className="pb-4">
          <p className="body">
            Not the future where everyone has a barcode on their head, but a nice future where technology is used to
            help people live a healthier and better life.
          </p>
          <p className="body">
            We have things like predictive inventory which means we&#8217;re never out of that thing you need. Our
            entire fulfillment process is smarter, safer and nicer. All of that simply means we can take better care of
            you.
          </p>
        </div>
      ),
      mobileImage: '/public/cell-3-mobile@2x.jpg',
    },
  ];

  const cellsJSX = map(cells, (c, i) => {
    const even = i % 2 === 0;

    const headClass = classNames([styles.cellImage, styles[`cellImage${i}`]]);

    return (
      <div className={headClass} key={i}>
        <img className="width-100 md-hide" src={c.mobileImage} alt="" />
        <Container className="max-width-4 px-4 lg-px-0 mx-auto pt-md-6 pb-md-7">
          <Row>
            {even ? <Col md={4} lg={5} className="sm-py-1 px-sm-3" /> : null}
            <Col md={8} lg={7} className="pr-sm-4 pr-md-5 pr-lg-7">
              {c.title}
              {c.header}
              {c.body}
            </Col>
          </Row>
        </Container>
      </div>
    );
  });

  return (
    <div id="about-content">
      <main className="sections">
        <div className="max-width-4 px-4 lg-px-0 mx-auto relative preserve-3d">
          <div className="parallax parallax-1">
            <InlineSVG
              className={classNames(
                styles.pill,
                styles.philosophyPillTriangle,
                'fill-teal',
                'animate-spin-160',
                'lg-show'
              )}
              src={pillTriangle}
            />
          </div>
        </div>

        <div className="max-width-4 px-3 lg-px-0 mx-auto sm-show">
          <div className="parallax parallax-2">
            <InlineSVG
              className={classNames(
                styles.pill,
                'lg-show',
                styles.aboutPillcrossedTl,
                'fill-yellow',
                'animate-spin-180'
              )}
              src={pillCrossed}
            />
          </div>

          <div className="parallax parallax-3">
            <InlineSVG className={classNames(styles.pill, 'lg-show', styles.aboutPillRed, 'fill-red')} src={pillTall} />
          </div>
        </div>

        <section className="section section--hero clearfix section--dark">
          <Container className="max-width-4 px-4 px-lg-7 mx-auto relative preserve-3d overflow-hidden">
            <Row className="pt-6 pb-5 relative mb-4">
              <Col md={4} className="md-show py-1 px-sm-3 pr-sm-4" />
              <Col md={7} className="px-sm-3 mb-5">
                <div className="font-label-callout font-down-3 mt-3 teal">ABOUT US</div>
                <h2 className="my-4 letter-spacing-025 font-h-3 white">
                  We are a new <br className="lg-show" /> kind of pharmacy
                </h2>

                <div className="body teal">
                  <p className="teal">
                    Or maybe a new kind of pharmacy mixed with an old type of pharmacy. Anything but a chain type of
                    pharmacy, where you wait in line to talk to people who would rather be anywhere else.
                  </p>
                  <p className="teal">
                    We are the type of pharmacy made up of friendly, caring pharmacists who carry prescriptions straight
                    to your door, at the tap of your phone. We are also people who take pleasure in looking after people
                    and hope to take care of you for years to come.
                  </p>

                  <br />

                  <p className="teal">
                    With love,
                    <br />
                    Capsule
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="section clearfix section--white relative" id="who-we-are">
          {cellsJSX}
        </section>

        <section className="clearfix section--light relative pb-4" id="how-it-works">
          <Container className="normal-container-padding px-4">
            <Row>
              <Col sm={12}>
                <div className="font-label-callout font-down-1 pt-6">HOW WE DO</div>
                <h2 className="mt-4 pb-3 font-up-7">Capsule is...</h2>
              </Col>
            </Row>
            <Row>
              <Col sm={12} lg={6} className="pr-4 lg-border-right border-teal lg-border-none">
                <div className="border-bottom border-teal pt-sm-1 pt-3">
                  <h3 className="xs-font-up-11 md-font-up-12 red pt-1">Faster</h3>
                  <div className="body pb-5">
                    Never wait in line for the pharmacy.
                    <br />
                    We bring your medications to your door.
                  </div>
                </div>

                <div className="border-bottom sm-border-bottom-none border-teal pt-sm-4 pt-3">
                  <h3 className="xs-font-up-11 md-font-up-12 red pt-1">Kinder</h3>
                  <div className="body pb-5">
                    Never talk to a voicemail autobot. Text, call, email, or chat with your pharmacist whenever is
                    convenient.
                  </div>
                </div>
              </Col>
              <Col sm={12} lg={6} className="pl-lg-4 pr-sm-4">
                <div className="border-bottom border-teal pt-sm-1 pt-3 pl-lg-4">
                  <h3 className="xs-font-up-11 md-font-up-12 red pt-1">Smarter</h3>
                  <div className="body pb-5">
                    Never worry about refills.
                    <br />
                    We keep track so you don&#8217;t have to.
                  </div>
                </div>
                <div className="pl-lg-4 pt-sm-4 pt-3">
                  <h3 className="xs-font-up-11 md-font-up-12 red pt-1">Better</h3>
                  <div className="body pb-5">
                    Never speak to an insurance company again.
                    <br />
                    We&#8217;ll coordinate with your doctor and insurance.
                  </div>
                </div>
              </Col>
            </Row>
          </Container>

          <div className="center pt-6 pb-4 sm-pb-0">
            <LinkWithQuery to={getStarted.toPath()} className="link-btn link-btn__primary link-btn__medium">
              JOIN CAPSULE
            </LinkWithQuery>
          </div>
        </section>
      </main>
    </div>
  );
};

export default compose(connector)(About);
